import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import { fetchVendorDraft, useCreateVendorDoc, useVendorDocKinds } from '@/lib/contractSigning';
import { useSubcontracts } from '@/lib/queries';
import type { ContractSigning, EsignDocKind, EsignKindDef, Uuid } from '@/lib/types';

/**
 * 협력사가 쓰는 서류 (V629) — 선급금 포기각서 · 보증이행완료확인서 · 기성 청구서.
 *
 * <p>칸은 서버가 정한 대로 선다. 처음 값은 고른 계약(과 기성 청구서면 그 회차)에서 채워 온다. [작성] 을 누르면 서명 대기로
 * 서고 곧바로 서명 창으로 이어진다 — 서명해야 본사로 간다.
 */
export function VendorDocModal({
  siteId,
  initialKind,
  claimId = null,
  onClose,
  onCreated,
}: {
  siteId: Uuid;
  initialKind?: EsignDocKind;
  claimId?: Uuid | null;
  onClose: () => void;
  onCreated: (signing: ContractSigning) => void;
}) {
  const { data: kinds = [] } = useVendorDocKinds(siteId);
  const { data: contracts = [] } = useSubcontracts(siteId);
  const create = useCreateVendorDoc(siteId);
  const [kind, setKind] = useState<EsignDocKind>(initialKind ?? 'PAYMENT_CLAIM');
  const [contractId, setContractId] = useState<Uuid>('');
  const [data, setData] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const chosenContract = contractId || contracts[0]?.id || '';
  const def: EsignKindDef | undefined = kinds.find((one) => one.kind === kind);

  useEffect(() => {
    if (!chosenContract) return;
    let alive = true;
    fetchVendorDraft(siteId, chosenContract, kind, claimId)
      .then((draft) => alive && setData(draft))
      .catch((cause) => alive && setMessage(cause instanceof Error ? cause.message : '불러오지 못했습니다.'));
    return () => {
      alive = false;
    };
  }, [siteId, chosenContract, kind, claimId]);

  const submit = () => {
    if (!chosenContract) return setMessage('계약을 골라 주세요.');
    const missing = def?.fields.find((field) => field.required && !(data[field.key] ?? '').trim());
    if (missing) return setMessage(`「${missing.label}」 을(를) 적어 주세요.`);
    setMessage(null);
    create.mutate(
      { kind, subcontractId: chosenContract, claimId, data },
      { onSuccess: onCreated, onError: (cause) => setMessage(cause instanceof Error ? cause.message : '만들지 못했습니다.') },
    );
  };

  return (
    <Modal
      title={def?.label ?? '서류 작성'}
      onClose={onClose}
      maxWidth="34rem"
      actions={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            닫기
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? '만드는 중…' : '작성하고 서명'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {kinds.map((one) => (
            <button
              key={one.kind}
              type="button"
              className={one.kind === kind ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setKind(one.kind)}
            >
              {one.label}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-ui-xs">
          <span style={{ color: 'var(--text-secondary)' }}>계약</span>
          <select className="input-field" value={chosenContract} onChange={(event) => setContractId(event.target.value)}>
            {contracts.length === 0 && <option value="">계약 없음</option>}
            {contracts.map((contract) => (
              <option key={contract.id} value={contract.id}>
                {[contract.trade, contract.workName].filter(Boolean).join(' · ')}
                {contract.contractNo ? ` (${contract.contractNo})` : ''}
              </option>
            ))}
          </select>
        </label>
        {(def?.fields ?? []).map((field) => (
          <label key={field.key} className="flex flex-col gap-1 text-ui-xs">
            <span style={{ color: 'var(--text-secondary)' }}>
              {field.label}
              {field.required && ' *'}
            </span>
            <DocField
              type={field.type}
              value={data[field.key] ?? ''}
              onChange={(value) => setData((current) => ({ ...current, [field.key]: value }))}
            />
          </label>
        ))}
        {message && (
          <div className="text-ui-xs" style={{ color: 'var(--danger, #dc2626)' }}>
            {message}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DocField({
  type,
  value,
  onChange,
}: {
  type: 'text' | 'area' | 'date' | 'money';
  value: string;
  onChange: (value: string) => void;
}) {
  if (type === 'area') {
    return <textarea className="input-field" rows={3} value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (type === 'date') {
    return <input className="input-field" type="date" value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (type === 'money') {
    const shown = value === '' ? '' : Number(value.replace(/[^0-9]/g, '') || 0).toLocaleString('ko-KR');
    return (
      <input
        className="input-field text-right"
        inputMode="numeric"
        value={shown}
        onChange={(event) => onChange(event.target.value.replace(/[^0-9]/g, ''))}
      />
    );
  }
  return <input className="input-field" value={value} onChange={(event) => onChange(event.target.value)} />;
}
